/**
 * The slice of a Microsoft Graph client this service uses. SPFx's MSGraphClientV3 and the
 * standard @microsoft/microsoft-graph-client Client both satisfy it, so the same code runs
 * in the SharePoint web part and in the web app.
 */
export interface IGraphRequest {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(): Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post(body: unknown): Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch(body: unknown): Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete(): Promise<any>;
}

export interface IGraphClient {
  api(path: string): IGraphRequest;
}

/** A SharePoint site the signed-in user can open. */
export interface ISiteInfo {
  /** Graph site ID: '{hostname},{siteCollectionId},{webId}'. */
  id: string;
  displayName: string;
  webUrl: string;
}

import type { ILibraryInfo } from '../models/LibraryInfo';
import { type IDriveItemRef, type IDriveWriter, NameConflictError } from '../engine/DriveWriter';

/** The subset of a Graph driveItem the scanner reads. */
export interface IDriveItem {
  id: string;
  name: string;
  size?: number;
  webUrl?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  createdBy?: { user?: { displayName?: string } };
  lastModifiedBy?: { user?: { displayName?: string } };
  parentReference?: { id?: string };
  file?: { mimeType?: string; hashes?: { quickXorHash?: string } };
  folder?: { childCount?: number };
  root?: object;
  deleted?: object;
}

interface IGraphPage<T> {
  value: T[];
  '@odata.nextLink'?: string;
  '@odata.deltaLink'?: string;
}

interface IGraphDrive {
  id: string;
  name: string;
  webUrl: string;
  driveType: string;
}

/** Folders the organizer must never change, as found by getProtectedFolders. */
export interface IFolderProtection {
  folders: { [folderId: string]: string };
  /** The check couldn't complete on a group site: protect every top-level folder to be safe. */
  protectAllTopLevel?: string;
}

export class ScanCancelledError extends Error {
  constructor() {
    super('Scan cancelled');
    // Keep instanceof working when compiled to ES5.
    Object.setPrototypeOf(this, ScanCancelledError.prototype);
  }
}

const DELTA_SELECT: string = [
  'id', 'name', 'size', 'webUrl', 'createdDateTime', 'lastModifiedDateTime',
  'createdBy', 'lastModifiedBy', 'parentReference', 'file', 'folder', 'root', 'deleted'
].join(',');

const MAX_ATTEMPTS: number = 5;
const RETRYABLE_STATUS: number[] = [429, 500, 502, 503, 504];
/**
 * Writes are retried only when Graph refused to run them (throttled). A 5xx after a write
 * may mean it succeeded, so the change engine re-checks state instead of blindly retrying.
 */
const RETRYABLE_WRITE_STATUS: number[] = [429];

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Seconds from a Retry-After header, when the Graph error exposes one. */
function getRetryAfterSeconds(error: unknown): number | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers: any = (error as any)?.headers;
  const value: string | null | undefined = headers && typeof headers.get === 'function'
    ? headers.get('Retry-After')
    : headers?.['Retry-After'];
  const seconds: number = Number(value);
  return value && !isNaN(seconds) ? seconds : undefined;
}

function getStatusCode(error: unknown): number | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (error as any)?.statusCode;
}

/** Encodes a library-relative path for Graph's path-based addressing ('/root:/a/b'). */
function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

interface IGraphItem {
  id: string;
  name: string;
  folder?: { childCount?: number };
  root?: object;
  parentReference?: { id?: string };
}

function toRef(item: IGraphItem): IDriveItemRef {
  return {
    id: item.id,
    name: item.name,
    parentId: item.root ? undefined : item.parentReference?.id,
    isFolder: !!(item.folder || item.root),
    childCount: item.folder?.childCount
  };
}

const ITEM_SELECT: string = '$select=id,name,folder,root,parentReference';

/** Microsoft Graph access for scanning and changing document libraries. */
export class GraphService {
  constructor(private readonly _client: IGraphClient) {}

  /** Sites the signed-in user can access, matching the search text ('' lists recent/popular ones). */
  public async searchSites(query: string): Promise<ISiteInfo[]> {
    const q: string = encodeURIComponent(query.trim() || '*');
    const page: IGraphPage<{ id: string; displayName?: string; name?: string; webUrl: string }> =
      await this._get(`/sites?search=${q}&$select=id,displayName,name,webUrl&$top=50`);
    return page.value
      // Personal OneDrive sites are out of scope.
      .filter(s => s.webUrl.indexOf('-my.sharepoint.') === -1)
      .map(s => ({ id: s.id, displayName: s.displayName || s.name || s.webUrl, webUrl: s.webUrl }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  /** Document libraries in the given site. siteId is '{hostname},{siteCollectionId},{webId}'. */
  public async getLibraries(siteId: string): Promise<ILibraryInfo[]> {
    const page: IGraphPage<IGraphDrive> = await this._get(
      `/sites/${siteId}/drives?$select=id,name,webUrl,driveType`
    );
    return page.value
      .filter(d => d.driveType === 'documentLibrary')
      .map(d => ({ id: d.id, name: d.name, webUrl: d.webUrl }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Every file and folder in a library, via the delta endpoint, which returns the whole
   * hierarchy as a flat paged list. onProgress receives the running item count.
   */
  public async getAllItems(
    driveId: string,
    onProgress?: (itemCount: number) => void,
    isCancelled?: () => boolean
  ): Promise<IDriveItem[]> {
    const items: IDriveItem[] = [];
    let url: string | undefined = `/drives/${driveId}/root/delta?$select=${DELTA_SELECT}`;

    while (url) {
      if (isCancelled && isCancelled()) {
        throw new ScanCancelledError();
      }
      const page: IGraphPage<IDriveItem> = await this._get(url);
      for (const item of page.value) {
        items.push(item);
      }
      if (onProgress) {
        onProgress(items.length);
      }
      url = page['@odata.nextLink'];
    }

    return items;
  }

  /**
   * In a site connected to Microsoft Teams, each standard channel's files live in a folder
   * of the site's library; moving or renaming it breaks the channel's Files tab. Finds those
   * folders by ID, which survives channel renames.
   */
  public async getProtectedFolders(driveId: string): Promise<IFolderProtection> {
    const unsure: IFolderProtection = {
      folders: {},
      protectAllTopLevel: 'Microsoft Teams channels could not be checked for this site, so top-level folders are protected to be safe.'
    };
    let drive: { owner?: { group?: { id?: string } } };
    try {
      drive = await this._get(`/drives/${driveId}?$select=id,owner`);
    } catch {
      return unsure;
    }
    const groupId: string | undefined = drive.owner?.group?.id;
    if (!groupId) {
      return { folders: {} };
    }

    let channels: IGraphPage<{ id: string; displayName: string }>;
    try {
      channels = await this._get(`/teams/${groupId}/channels?$select=id,displayName`);
    } catch (error) {
      if (getStatusCode(error) === 404) {
        return { folders: {} }; // A Microsoft 365 group without a team.
      }
      return unsure;
    }

    const folders: { [folderId: string]: string } = {};
    for (const channel of channels.value) {
      try {
        const folder: { id: string; parentReference?: { driveId?: string } } =
          await this._get(`/teams/${groupId}/channels/${channel.id}/filesFolder?$select=id,parentReference`);
        if (folder.parentReference?.driveId === driveId) {
          folders[folder.id] = `Used by the Teams channel "${channel.displayName}". Moving or renaming it would break the channel's Files tab.`;
        }
      } catch {
        // The channel's folder isn't created until someone opens its Files tab; nothing to protect yet.
      }
    }
    return { folders };
  }

  /** Write access to one library for the change engine. */
  public createDriveWriter(driveId: string): IDriveWriter {
    const base: string = `/drives/${driveId}`;
    const getOrUndefined = async (url: string): Promise<IDriveItemRef | undefined> => {
      try {
        return toRef(await this._get<IGraphItem>(url));
      } catch (error) {
        if (getStatusCode(error) === 404) {
          return undefined;
        }
        throw error;
      }
    };
    const rethrowConflict = (error: unknown, name: string): never => {
      if (getStatusCode(error) === 409) {
        throw new NameConflictError(name);
      }
      throw error;
    };

    return {
      getRoot: async () => toRef(await this._get<IGraphItem>(`${base}/root?${ITEM_SELECT}`)),
      getItem: itemId => getOrUndefined(`${base}/items/${itemId}?${ITEM_SELECT}`),
      getChild: (parentId, name) => getOrUndefined(`${base}/items/${parentId}:/${encodePath(name)}?${ITEM_SELECT}`),
      createFolder: async (parentId, name) => {
        try {
          return toRef(await this._write<IGraphItem>('post', `${base}/items/${parentId}/children`, {
            name,
            folder: {},
            '@microsoft.graph.conflictBehavior': 'fail'
          }));
        } catch (error) {
          return rethrowConflict(error, name);
        }
      },
      deleteItem: async itemId => {
        await this._withRetry(() => this._client.api(`${base}/items/${itemId}`).delete(), RETRYABLE_WRITE_STATUS);
      },
      moveItem: async (itemId, newParentId, newName) => {
        const body: { [key: string]: unknown } = {};
        if (newParentId) {
          body.parentReference = { id: newParentId };
        }
        if (newName) {
          body.name = newName;
        }
        try {
          return toRef(await this._write<IGraphItem>(
            'patch', `${base}/items/${itemId}?@microsoft.graph.conflictBehavior=fail`, body));
        } catch (error) {
          return rethrowConflict(error, newName ?? '');
        }
      }
    };
  }

  /** GET with retries on throttling and transient errors, honouring Retry-After. */
  private async _get<T>(url: string): Promise<T> {
    return this._withRetry(() => this._client.api(url).get(), RETRYABLE_STATUS);
  }

  private async _write<T>(method: 'post' | 'patch', url: string, body: object): Promise<T> {
    return this._withRetry(() => this._client.api(url)[method](body), RETRYABLE_WRITE_STATUS);
  }

  private async _withRetry<T>(request: () => Promise<T>, retryable: number[]): Promise<T> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await request();
      } catch (error) {
        const status: number | undefined = getStatusCode(error);
        if (attempt >= MAX_ATTEMPTS || status === undefined || retryable.indexOf(status) === -1) {
          throw error;
        }
        const waitSeconds: number = getRetryAfterSeconds(error) ?? Math.pow(2, attempt);
        await delay(waitSeconds * 1000);
      }
    }
  }
}
